    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Readiness" title="Logistics Workforce Readiness" subtitle="Evaluate whether employees, drivers, warehouse workers, and clearance brokers can be assigned to logistics work.">
        <div actions>
          <button pButton type="button" label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="load()" [disabled]="loading"></button>
        </div>
      </app-page-hero>

      @if (loading) {
        <p-progressBar mode="indeterminate"></p-progressBar>
      }

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'snapshots', label: 'Readiness Snapshots', icon: 'fa-solid fa-shield-check', badge: snapshots.length },
          { key: 'rules', label: 'Rules', icon: 'fa-solid fa-list-check', badge: rules.length },
          { key: 'overrides', label: 'Overrides', icon: 'fa-solid fa-shield-halved', badge: overrides.length },
          { key: 'qualifications', label: 'Driver Qualifications', icon: 'fa-solid fa-id-card-clip', badge: qualifications.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <app-hr-action-dialog [visible]="actionDialogVisible('evaluation')" (visibleChange)="setActionDialogVisible('evaluation', $event)" title="Run Assignment Evaluation" eyebrow="Workforce Readiness" subtitle="Evaluate one worker or the tenant workforce against operational assignment rules." icon="fa-solid fa-route" [contextTitle]="evaluationRequest.assignmentType ? format(evaluationRequest.assignmentType) : 'Select an assignment type'" contextSubtitle="Results are cached for dispatch and other operational consumers.">
        <app-page-form-section icon="fa-solid fa-route" title="Assignment Evaluation" subtitle="Run readiness checks for a specific employee or driver, or evaluate the whole HR workforce for a logistics assignment type.">
          <div class="form-grid compact">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="assignmentType" [options]="assignmentOptions" optionLabel="label" optionValue="value" [(ngModel)]="evaluationRequest.assignmentType" styleClass="w-full"></p-select>
                <label for="assignmentType">Assignment type</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="employeeId" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="evaluationRequest.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="employeeId">Employee</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-truck-fast"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="driverId" [options]="driverOptions" optionLabel="label" optionValue="value" [(ngModel)]="evaluationRequest.driverId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="driverId">Driver</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-link"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="assignmentResourceId" pInputText type="number" [(ngModel)]="evaluationRequest.assignmentResourceId" />
                <label for="assignmentResourceId">Assignment resource ID</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="assignmentStartDate" pInputText type="date" [(ngModel)]="evaluationRequest.assignmentStartDate" />
                <label for="assignmentStartDate">Assignment start</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-check"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="assignmentEndDate" pInputText type="date" [(ngModel)]="evaluationRequest.assignmentEndDate" />
                <label for="assignmentEndDate">Assignment end</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-user-gear"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="requiredWorkerCategory" [options]="workerCategoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="evaluationRequest.requiredWorkerCategory" [showClear]="true" styleClass="w-full"></p-select>
                <label for="requiredWorkerCategory">Required worker category</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-bolt"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="forceRefresh" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="evaluationRequest.forceRefresh" styleClass="w-full"></p-select>
                <label for="forceRefresh">Refresh cached snapshot</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="form-actions">
            <button pButton type="button" icon="fa-solid fa-play" label="Run Evaluation" (click)="evaluate()" [disabled]="loading || !canManageReadiness"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetEvaluation()"></button>
          </div>

          @if (evaluationResult) {
            <div class="evaluation-summary">
              <div><span>Evaluated</span><strong>{{ evaluationResult.evaluatedEmployees ?? 0 }}</strong></div>
              <div><span>Ready</span><strong class="good">{{ evaluationResult.readyEmployees ?? 0 }}</strong></div>
              <div><span>Warning</span><strong class="warn">{{ evaluationResult.warningEmployees ?? 0 }}</strong></div>
              <div><span>Blocked</span><strong class="bad">{{ evaluationResult.blockedEmployees ?? 0 }}</strong></div>
            </div>
          }
        </app-page-form-section>
      </app-hr-action-dialog>

      <section class="readiness-top-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'snapshots'" title="Readiness Filters" subtitle="Filter cached snapshots by assignment type and readiness state." [resultText]="snapshots.length + ' snapshots'">
          <div class="filter-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="filterAssignmentType" [options]="assignmentOptions" optionLabel="label" optionValue="value" [(ngModel)]="filterAssignmentType" [showClear]="true" styleClass="w-full" (onChange)="load()"></p-select>
                <label for="filterAssignmentType">Assignment type</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-signal"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="filterReadinessStatus" [options]="readinessStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="filterReadinessStatus" [showClear]="true" styleClass="w-full" (onChange)="load()"></p-select>
                <label for="filterReadinessStatus">Readiness status</label>
              </p-iftaLabel>
            </p-inputGroup>

            <button pButton type="button" label="Clear" icon="pi pi-filter-slash" severity="secondary" [outlined]="true" (click)="clearFilters()"></button>
          </div>

          <div class="adapter-list" aria-label="Phase 4 logistics adapters">
            <span><i class="fa-solid fa-truck-ramp-box"></i> Shipment delivery</span>
            <span><i class="fa-solid fa-truck-moving"></i> Fleet mobilization</span>
            <span><i class="fa-solid fa-warehouse"></i> Warehouse shift</span>
            <span><i class="fa-solid fa-file-contract"></i> Clearance assignment</span>
          </div>
        </app-page-data-panel>
      </section>

      <app-page-data-panel [hidden]="activeListTab !== 'snapshots'" title="Cached Readiness Snapshots" subtitle="Dispatch-heavy screens can consume these cached results until the snapshot expires." [resultText]="dashboard.owningCompanyId ? 'Tenant ' + dashboard.owningCompanyId : 'Tenant scoped'">
        @if (canManageReadiness) {
          <div actions><button pButton type="button" label="Run Evaluation" icon="fa-solid fa-clipboard-check" (click)="openActionDialog('evaluation')" [disabled]="loading"></button></div>
        }
        <p-table [value]="snapshots" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1280px' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]">
          <ng-template pTemplate="header">
            <tr>
              <th>Worker</th>
              <th>Assignment</th>
              <th>Status</th>
              <th>Checks</th>
              <th>Source</th>
              <th>Cache</th>
              <th>Rule Results</th>
              <th style="width: 5rem">Open</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-snapshot>
            <tr>
              <td><strong>{{ snapshot.employeeName || '-' }}</strong><br><small>{{ snapshot.employeeNumber || snapshot.employeeId }}</small></td>
              <td><strong>{{ format(snapshot.assignmentType) }}</strong><br><small>{{ snapshot.assignmentResourceId ? 'Resource ' + snapshot.assignmentResourceId : 'General pool' }}</small></td>
              <td><p-tag [value]="format(snapshot.readinessStatus)" [severity]="readinessSeverity(snapshot.readinessStatus)"></p-tag></td>
              <td>{{ snapshot.blockerCount ?? 0 }} blockers<br><small>{{ snapshot.warningCount ?? 0 }} warnings</small></td>
              <td>{{ format(snapshot.sourceModule) }}<br><small>{{ snapshot.sourceResourceId || '-' }}</small></td>
              <td>{{ snapshot.evaluatedAt | date:'short' }}<br><small>Expires {{ snapshot.expiresAt | date:'short' }}</small></td>
              <td>
                <div class="rule-result-list">
                  @for (result of ruleResults(snapshot).slice(0, 3); track result.ruleCode) {
                    <span [class.blocker]="result.blocker" [class.warning]="result.warning">{{ format(result.ruleCode) }}</span>
                  }
                  @if (!ruleResults(snapshot).length) { <small>No rule results</small> }
                </div>
              </td>
              <td>
                <div class="row-actions">
                  <button pButton type="button" icon="pi pi-eye" [rounded]="true" [text]="true" (click)="openSnapshot(snapshot)"></button>
                  <button pButton type="button" icon="pi pi-arrow-right" [rounded]="true" [text]="true" [routerLink]="['/hr/employees', snapshot.employeeId]"></button>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8">No workforce readiness snapshots found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      <section class="management-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'rules'" title="Readiness Rules" subtitle="Tenant-owned rules are evaluated in order for logistics assignments." [resultText]="rules.length + ' rules'">
          @if (canManageReadiness) {
            <div actions><button pButton type="button" label="New Rule" icon="fa-solid fa-plus" (click)="openActionDialog('rule')"></button></div>
          }
          <p-table [value]="rules" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '980px' }">
            <ng-template pTemplate="header">
              <tr><th>Rule</th><th>Assignment</th><th>Worker</th><th>Severity</th><th>Cache</th><th>Status</th><th style="width: 5rem"></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-rule>
              <tr>
                <td><strong>{{ rule.ruleName }}</strong><br><small>{{ rule.ruleCode }}</small></td>
                <td>{{ format(rule.assignmentType) }}</td>
                <td>{{ format(rule.workerCategory) }}</td>
                <td><p-tag [value]="format(rule.severity)" [severity]="rule.severity === 'BLOCKER' ? 'danger' : 'warn'"></p-tag></td>
                <td>{{ rule.cacheTtlMinutes ?? 15 }} min</td>
                <td><p-tag [value]="rule.active ? 'Active' : 'Inactive'" [severity]="rule.active ? 'success' : 'secondary'"></p-tag></td>
                <td>
                  @if (canManageReadiness) {
                    <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="editRule(rule)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="7">No readiness rules configured.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'overrides'" title="Active Overrides" subtitle="Approved temporary overrides are visible to dispatch and readiness consumers." [resultText]="overrides.length + ' overrides'">
          @if (canOverrideReadiness) {
            <div actions><button pButton type="button" label="New Override" icon="fa-solid fa-plus" (click)="openActionDialog('override')"></button></div>
          }
          <p-table [value]="overrides" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1060px' }">
            <ng-template pTemplate="header">
              <tr><th>Worker</th><th>Assignment</th><th>Status</th><th>Window</th><th>Reason</th><th style="width: 5rem"></th></tr>
            </ng-template>
            <ng-template pTemplate="body" let-override>
              <tr>
                <td><strong>{{ override.employeeName || '-' }}</strong><br><small>{{ override.employeeNumber || override.employeeId }}</small></td>
                <td>{{ format(override.assignmentType) }}<br><small>{{ override.assignmentResourceId || 'Any resource' }}</small></td>
                <td><p-tag [value]="format(override.overrideStatus)" [severity]="override.overrideStatus === 'ACTIVE' ? 'warn' : 'secondary'"></p-tag></td>
                <td>{{ override.validFrom | date:'short' }}<br><small>Until {{ override.validUntil | date:'short' }}</small></td>
                <td>{{ override.reason || '-' }}</td>
                <td>
                  @if (canOverrideReadiness && override.overrideStatus === 'ACTIVE' && override.readinessOverrideId) {
                    <button pButton type="button" icon="pi pi-ban" severity="danger" [rounded]="true" [text]="true" (click)="revokeOverride(override)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="6">No readiness overrides found.</td></tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>

      @if (canManageReadiness || canOverrideReadiness || canManageQualifications) {
        <section class="form-stack">
          @if (canManageReadiness) {
            <app-hr-action-dialog [visible]="actionDialogVisible('rule')" (visibleChange)="setActionDialogVisible('rule', $event)" [title]="ruleForm.readinessRuleId ? 'Edit Readiness Rule' : 'New Readiness Rule'" eyebrow="Readiness Setup" subtitle="Configure a tenant-owned rule used by assignment evaluation." icon="fa-solid fa-sliders" [contextTitle]="ruleForm.ruleName || ruleForm.ruleCode || 'New evaluation rule'" contextSubtitle="Rules are evaluated in priority order and cached for operational use.">
              <app-page-form-section icon="fa-solid fa-sliders" title="Rule Maintenance" subtitle="Create or adjust tenant readiness rules used by the evaluator.">
              <div class="form-grid">
                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-code"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <input id="ruleCode" pInputText [(ngModel)]="ruleForm.ruleCode" />
                    <label for="ruleCode">Rule code</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <input id="ruleName" pInputText [(ngModel)]="ruleForm.ruleName" />
                    <label for="ruleName">Rule name</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="ruleAssignmentType" [options]="assignmentOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleForm.assignmentType" [showClear]="true" styleClass="w-full"></p-select>
                    <label for="ruleAssignmentType">Assignment type</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-user-gear"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="ruleWorkerCategory" [options]="workerCategoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleForm.workerCategory" [showClear]="true" styleClass="w-full"></p-select>
                    <label for="ruleWorkerCategory">Worker category</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-triangle-exclamation"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="ruleSeverity" [options]="severityOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleForm.severity" styleClass="w-full"></p-select>
                    <label for="ruleSeverity">Severity</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-clock"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <input id="cacheTtlMinutes" pInputText type="number" [(ngModel)]="ruleForm.cacheTtlMinutes" />
                    <label for="cacheTtlMinutes">Cache TTL minutes</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="ruleActive" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="ruleForm.active" styleClass="w-full"></p-select>
                    <label for="ruleActive">Active</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup class="wide">
                  <p-inputGroupAddon><i class="fa-solid fa-brackets-curly"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <textarea id="ruleConfigJson" pTextarea rows="3" [(ngModel)]="ruleForm.ruleConfigJson"></textarea>
                    <label for="ruleConfigJson">Rule config JSON</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup class="wide">
                  <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <textarea id="ruleDescription" pTextarea rows="3" [(ngModel)]="ruleForm.description"></textarea>
                    <label for="ruleDescription">Description</label>
                  </p-iftaLabel>
                </p-inputGroup>
              </div>

              <div class="form-actions">
                <button pButton type="button" icon="pi pi-save" label="Save Rule" (click)="saveRule()" [disabled]="!ruleForm.ruleCode || !ruleForm.ruleName"></button>
                <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetRule()"></button>
              </div>
              </app-page-form-section>
            </app-hr-action-dialog>
          }

          @if (canOverrideReadiness) {
            <app-hr-action-dialog [visible]="actionDialogVisible('override')" (visibleChange)="setActionDialogVisible('override', $event)" title="Create Readiness Override" eyebrow="Operational Exception" subtitle="Authorize a temporary, traceable exception for a specific worker." icon="fa-solid fa-shield-halved" [contextTitle]="overrideForm.employeeId ? 'Employee ' + overrideForm.employeeId : 'Select an employee'" contextSubtitle="Overrides expire automatically and remain visible to dispatch consumers.">
              <app-page-form-section icon="fa-solid fa-shield-halved" title="Override Readiness" subtitle="Create a time-bound dispatch override for exceptional operations.">
              <div class="form-grid compact">
                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="overrideEmployeeId" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="overrideForm.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                    <label for="overrideEmployeeId">Employee</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <p-select inputId="overrideAssignmentType" [options]="assignmentOptions" optionLabel="label" optionValue="value" [(ngModel)]="overrideForm.assignmentType" [showClear]="true" styleClass="w-full"></p-select>
                    <label for="overrideAssignmentType">Assignment type</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-link"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <input id="overrideAssignmentResourceId" pInputText type="number" [(ngModel)]="overrideForm.assignmentResourceId" />
                    <label for="overrideAssignmentResourceId">Assignment resource ID</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup>
                  <p-inputGroupAddon><i class="fa-solid fa-calendar-clock"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <input id="overrideValidUntil" pInputText type="datetime-local" [(ngModel)]="overrideValidUntilLocal" />
                    <label for="overrideValidUntil">Valid until</label>
                  </p-iftaLabel>
                </p-inputGroup>

                <p-inputGroup class="wide">
                  <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
                  <p-iftaLabel>
                    <textarea id="overrideReason" pTextarea rows="3" [(ngModel)]="overrideForm.reason"></textarea>
                    <label for="overrideReason">Reason</label>
                  </p-iftaLabel>
                </p-inputGroup>
              </div>

              <div class="form-actions">
                <button pButton type="button" icon="pi pi-save" label="Create Override" (click)="saveOverride()" [disabled]="!overrideForm.employeeId || !overrideForm.reason"></button>
                <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetOverride()"></button>
              </div>
              </app-page-form-section>
            </app-hr-action-dialog>
          }
        </section>
      }

      <app-page-data-panel [hidden]="activeListTab !== 'qualifications'" title="Driver Qualifications" subtitle="Driver qualification status feeds readiness checks for delivery and mobilization assignments." [resultText]="qualifications.length + ' qualifications'">
        @if (canManageQualifications) {
          <div actions><button pButton type="button" label="New Qualification" icon="fa-solid fa-plus" (click)="openActionDialog('qualification')"></button></div>
        }
        <p-table [value]="qualifications" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1240px' }" [paginator]="true" [rows]="20" [rowsPerPageOptions]="[20, 50, 100]">
          <ng-template pTemplate="header">
            <tr><th>Worker</th><th>Driver</th><th>Qualification</th><th>Status</th><th>License</th><th>Validity</th><th>Source</th><th style="width: 5rem"></th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-qualification>
            <tr>
              <td><strong>{{ qualification.employeeName || '-' }}</strong><br><small>{{ qualification.employeeNumber || qualification.employeeId }}</small></td>
              <td>{{ qualification.driverId || '-' }}</td>
              <td><strong>{{ qualification.qualificationName || '-' }}</strong><br><small>{{ qualification.qualificationType || '-' }}</small></td>
              <td><p-tag [value]="format(qualification.qualificationStatus)" [severity]="qualificationSeverity(qualification.qualificationStatus)"></p-tag></td>
              <td>{{ qualification.licenseNumber || '-' }}<br><small>{{ qualification.documentTypeCode || '-' }}</small></td>
              <td>{{ qualification.validFrom || '-' }}<br><small>Until {{ qualification.validUntil || '-' }}</small></td>
              <td>{{ format(qualification.sourceModule) }}<br><small>{{ qualification.sourceResourceId || '-' }}</small></td>
              <td>
                @if (canManageQualifications) {
                  <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" (click)="editQualification(qualification)"></button>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="8">No driver qualifications found.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>

      @if (canManageQualifications) {
        <app-hr-action-dialog [visible]="actionDialogVisible('qualification')" (visibleChange)="setActionDialogVisible('qualification', $event)" [title]="qualificationForm.driverQualificationId ? 'Edit Driver Qualification' : 'New Driver Qualification'" eyebrow="Driver Readiness" subtitle="Maintain a qualification that participates in delivery and mobilization checks." icon="fa-solid fa-id-card-clip" [contextTitle]="qualificationForm.qualificationName || 'Driver qualification'" [contextSubtitle]="qualificationForm.employeeId ? 'Employee ' + qualificationForm.employeeId : 'Select an employee'">
          <app-page-form-section icon="fa-solid fa-id-card-clip" title="Qualification Maintenance" subtitle="Maintain driver readiness qualifications without storing sensitive document images in Phase 4.">
          <div class="form-grid">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="qualificationEmployeeId" [options]="employeeOptions" optionLabel="label" optionValue="value" [(ngModel)]="qualificationForm.employeeId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="qualificationEmployeeId">Employee</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-truck-fast"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="qualificationDriverId" [options]="driverOptions" optionLabel="label" optionValue="value" [(ngModel)]="qualificationForm.driverId" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                <label for="qualificationDriverId">Driver</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-certificate"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="qualificationType" pInputText [(ngModel)]="qualificationForm.qualificationType" />
                <label for="qualificationType">Qualification type</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-heading"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="qualificationName" pInputText [(ngModel)]="qualificationForm.qualificationName" />
                <label for="qualificationName">Qualification name</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-signal"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="qualificationStatus" [options]="qualificationStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="qualificationForm.qualificationStatus" styleClass="w-full"></p-select>
                <label for="qualificationStatus">Status</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="licenseNumber" pInputText [(ngModel)]="qualificationForm.licenseNumber" />
                <label for="licenseNumber">License number</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-file-shield"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="documentTypeCode" pInputText [(ngModel)]="qualificationForm.documentTypeCode" />
                <label for="documentTypeCode">Document type code</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-landmark"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="issuingAuthority" pInputText [(ngModel)]="qualificationForm.issuingAuthority" />
                <label for="issuingAuthority">Issuing authority</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-day"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="validFrom" pInputText type="date" [(ngModel)]="qualificationForm.validFrom" />
                <label for="validFrom">Valid from</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-calendar-xmark"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="validUntil" pInputText type="date" [(ngModel)]="qualificationForm.validUntil" />
                <label for="validUntil">Valid until</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-toggle-on"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="qualificationActive" [options]="booleanOptions" optionLabel="label" optionValue="value" [(ngModel)]="qualificationForm.active" styleClass="w-full"></p-select>
                <label for="qualificationActive">Active</label>
              </p-iftaLabel>
            </p-inputGroup>

            <p-inputGroup class="wide">
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="qualificationNotes" pTextarea rows="3" [(ngModel)]="qualificationForm.notes"></textarea>
                <label for="qualificationNotes">Notes</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>

          <div class="form-actions">
            <button pButton type="button" icon="pi pi-save" label="Save Qualification" (click)="saveQualification()" [disabled]="!qualificationForm.employeeId || !qualificationForm.qualificationType || !qualificationForm.qualificationName"></button>
            <button pButton type="button" icon="pi pi-undo" label="Reset" severity="secondary" [outlined]="true" (click)="resetQualification()"></button>
          </div>
          </app-page-form-section>
        </app-hr-action-dialog>
      }
    </app-page-shell>

    <p-dialog
      header="Readiness Snapshot"
      [(visible)]="snapshotDialogVisible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: 'min(980px, 96vw)' }">
      @if (selectedSnapshot) {
        <div class="snapshot-dialog">
          <div class="snapshot-summary-grid">
            <div>
              <span>Worker</span>
              <strong>{{ selectedSnapshot.employeeName || '-' }}</strong>
              <small>{{ selectedSnapshot.employeeNumber || selectedSnapshot.employeeId }}</small>
            </div>
            <div>
              <span>Assignment</span>
              <strong>{{ format(selectedSnapshot.assignmentType) }}</strong>
              <small>{{ selectedSnapshot.assignmentResourceId ? 'Resource ' + selectedSnapshot.assignmentResourceId : 'General pool' }}</small>
            </div>
            <div>
              <span>Status</span>
              <p-tag [value]="format(selectedSnapshot.readinessStatus)" [severity]="readinessSeverity(selectedSnapshot.readinessStatus)"></p-tag>
              <small>{{ selectedSnapshot.blockerCount ?? 0 }} blockers, {{ selectedSnapshot.warningCount ?? 0 }} warnings</small>
            </div>
            <div>
              <span>Cache</span>
              <strong>{{ selectedSnapshot.evaluatedAt | date:'short' }}</strong>
              <small>Expires {{ selectedSnapshot.expiresAt | date:'short' }}</small>
            </div>
          </div>

          <p-table [value]="selectedSnapshotRuleResults" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '820px' }">
            <ng-template pTemplate="header">
              <tr>
                <th>Rule</th>
                <th>Severity</th>
                <th>Result</th>
                <th>Message</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-result>
              <tr>
                <td><strong>{{ result.ruleName || format(result.ruleCode) }}</strong><br><small>{{ result.ruleCode || '-' }}</small></td>
                <td><p-tag [value]="format(result.severity)" [severity]="result.severity === 'BLOCKER' ? 'danger' : 'warn'"></p-tag></td>
                <td><p-tag [value]="format(result.resultStatus)" [severity]="ruleResultSeverity(result)"></p-tag></td>
                <td>{{ result.message || '-' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4">No rule result payload was stored for this snapshot.</td></tr>
            </ng-template>
          </p-table>
        </div>
      }
    </p-dialog>
